import type { Perk } from 'ui/components/PerksList/PerksList';
import type { Document } from '@contentful/rich-text-types';
import { PlanTypes } from 'ui/utils/constants/maverick';
import type { Region } from '@whoop/i18n/types/internationalization';

export enum MembershipTier {
  ONE = 'ONE',
  PEAK = 'PEAK',
  LIFE = 'LIFE',
}

export const TRIAL = 'TRIAL';
export type MembershipTierWithTrial = MembershipTier | typeof TRIAL;

export enum ProductType {
  MEMBERSHIP = 'membership',
  DEVICE = 'device',
  UPGRADE_FEE = 'upgrade-fee',
  EXTENSION = 'extension',
  UPCYCLE_KIT = 'upcycle-kit',
  UPCYCLE_MEMBERSHIP = 'upcycle-membership',
  BAND = 'wrist-band',
  MCB = 'milestone-collectors-box',
  MCB_ENGRAVING = 'mcb-engraving',
  INCLUDED_BAND = 'included-band',
  LIFETIME_WARRANTY = 'lifetime-warranty',
}

// this ordering is used to order tiers on JF + UF compare table
export const ALL_MEMBERSHIP_TIERS = [
  MembershipTier.ONE,
  MembershipTier.PEAK,
  MembershipTier.LIFE,
];

export const tierDisplayName = {
  [MembershipTier.ONE]: 'One',
  [MembershipTier.PEAK]: 'Peak',
  [MembershipTier.LIFE]: 'Life',
};

export const tierDisplayNameWithTrial = {
  ...tierDisplayName,
  TRIAL: 'Trial',
};

export enum DeviceGeneration {
  HARVARD = 'HARVARD',
  GOOSE = 'GOOSE',
  MAVERICK = 'MAVERICK',
}

export const GEN_4_DEVICE = '4.0';
export const GEN_5_DEVICE = '5.0';
export const GEN_MG_DEVICE = 'MG';

// "ONE" -> "GOOSE"
export const membershipTierToDevice = {
  [MembershipTier.ONE]: DeviceGeneration.GOOSE,
  [MembershipTier.PEAK]: DeviceGeneration.GOOSE,
  [MembershipTier.LIFE]: DeviceGeneration.MAVERICK,
  [TRIAL]: DeviceGeneration.HARVARD,
};

// "HARVARD" -> "4.0"
export const deviceGenerationToDisplayName = {
  [DeviceGeneration.HARVARD]: GEN_4_DEVICE,
  [DeviceGeneration.GOOSE]: GEN_5_DEVICE,
  [DeviceGeneration.MAVERICK]: GEN_MG_DEVICE,
};

// "ONE" -> "5.0"
export const membershipTierToDeviceDisplayName = {
  [MembershipTier.ONE]: GEN_5_DEVICE,
  [MembershipTier.PEAK]: GEN_5_DEVICE,
  [MembershipTier.LIFE]: GEN_MG_DEVICE,
};

export enum VARIANT_ATTRIBUTE_NAMES {
  TIER = 'membership-tier-type',
  BILLING_TYPE = 'billing-type',
  FAMILY_SIZE = 'family-size',
  TRIAL_DAYS = 'trialDays',
  COMPATIBLE_DEVICES = 'compatible-devices',
  MONTHS = 'months',
  SUBSCRIPTION_TYPE = 'subscriptionType',
}

export const MEMBERSHIP_TIER_ATTRIBUTE_TRIAL_VALUE = 'trial';

const ONE_DEVICE_SKU = '970-004-000';
const PEAK_DEVICE_SKU = '970-005-000';
const LIFE_DEVICE_SKU = '970-003-000';

export const DeviceSkuByTier = {
  [MembershipTier.ONE]: ONE_DEVICE_SKU,
  [MembershipTier.PEAK]: PEAK_DEVICE_SKU,
  [MembershipTier.LIFE]: LIFE_DEVICE_SKU,
};

export const MembershipIdentifyingAttributes = {
  [PlanTypes.ANNUAL]: {
    trialDays: 365,
    isFamily: false,
  },
  [PlanTypes.TWO_YEAR]: {
    trialDays: 730,
    isFamily: false,
  },
  [PlanTypes.TRIAL]: {
    trialDays: 30,
    isFamily: false,
  },
  [PlanTypes.FAMILY]: {
    trialDays: 365,
    isFamily: true,
  },
};

// User is on JF
export const ONE_PERKS: Perk[] = [
  { key: 'fourteen-day-battery', iconStyle: 'check' },
  { key: 'sleep-recovery-strain', iconStyle: 'check' },
  { key: 'personalized-coaching', iconStyle: 'check' },
  { key: 'vo2Max', iconStyle: 'check' },
  { key: 'womens-health', iconStyle: 'check' },
];

// User is on the upgrade flow and has current tier = ONE
export const ONE_UPGRADE_PERKS: Perk[] = [
  { key: 'fourteen-day-battery', iconStyle: 'check' },
  { key: 'new-wired-charger', iconStyle: 'check' },
  { key: 'sleep-recovery-strain', iconStyle: 'check' },
  { key: 'personalized-coaching', iconStyle: 'check' },
  { key: 'vo2Max', iconStyle: 'check' },
  { key: 'womens-health', iconStyle: 'check' },
];

// User is on JF
const BASE_PEAK_PERKS = [
  { key: 'fourteen-day-battery', iconStyle: 'check' },
  { key: 'health-span', iconStyle: 'check' },
  { key: 'health-monitor', iconStyle: 'check' },
  { key: 'realtime-stress', iconStyle: 'check' },
];
export const PEAK_PERKS: Perk[] = [
  { key: 'one-plus', iconStyle: 'none' },
  ...BASE_PEAK_PERKS,
];
export const VERTICAL_STACK_PEAK_PERKS: Perk[] = [
  ...BASE_PEAK_PERKS,
  { key: 'plus-one', iconStyle: 'check' },
];

// User is on the upgrade flow and has current tier = ONE
// Peak card mentions ONE
const BASE_PEAK_UPGRADE_PERKS_ONE_PRESENT: Perk[] = [
  { key: 'fourteen-day-battery', iconStyle: 'check' },
  { key: 'new-wireless-powerpack', iconStyle: 'check' },
  { key: 'health-span', iconStyle: 'check' },
  { key: 'health-monitor', iconStyle: 'check' },
  { key: 'realtime-stress', iconStyle: 'check' },
];
export const PEAK_UPGRADE_PERKS_ONE_PRESENT: Perk[] = [
  { key: 'one-plus', iconStyle: 'none' },
  ...BASE_PEAK_UPGRADE_PERKS_ONE_PRESENT,
];
export const VERTICAL_STACK_PEAK_UPGRADE_PERKS_ONE_PRESENT: Perk[] = [
  ...BASE_PEAK_UPGRADE_PERKS_ONE_PRESENT,
  { key: 'plus-one', iconStyle: 'check' },
];

// User is on the upgrade flow and has current tier = PEAK
// Peak card should NOT mention ONE
export const PEAK_UPGRADE_PERKS_ONE_NOT_PRESENT: Perk[] = [
  { key: 'get-the-latest-tech', iconStyle: 'none' },
  { key: 'fourteen-day-battery', iconStyle: 'check' },
  { key: 'new-wireless-powerpack', iconStyle: 'check' },
  { key: 'health-span-aging', iconStyle: 'check' },
  { key: 'all-existing-benefits', iconStyle: 'check' },
];

//User is on JF or upgrade flow
const BASE_LIFE_PERKS: Perk[] = [
  { key: 'fourteen-day-battery-MG', iconStyle: 'check' },
  { key: 'daily-blood-pressure', iconStyle: 'check' },
  { key: 'ecg-readings', iconStyle: 'check' },
  { key: 'irregular-heart', iconStyle: 'check' },
];
export const LIFE_PERKS: Perk[] = [
  { key: 'peak-plus', iconStyle: 'none' },
  ...BASE_LIFE_PERKS,
];

export const VERTICAL_STACK_LIFE_PERKS: Perk[] = [
  ...BASE_LIFE_PERKS,
  { key: 'plus-peak', iconStyle: 'check' },
];

export const LIFE_PERKS_NO_ECG: Perk[] = [
  ...LIFE_PERKS.filter((perk) => perk.key !== 'irregular-heart'),
  { key: 'on-demand-afib-detection', iconStyle: 'check' },
];

export const VERTICAL_STACK_LIFE_PERKS_NO_ECG: Perk[] = [
  ...BASE_LIFE_PERKS.filter((perk) => perk.key !== 'irregular-heart'),
  { key: 'on-demand-afib-detection', iconStyle: 'check' },
  { key: 'plus-peak', iconStyle: 'check' },
];

export const TRIAL_PERKS: Perk[] = [
  { key: 'device-4.0', iconStyle: 'check' },
  { key: 'all-benefits-excluding', iconStyle: 'check' },
  { key: 'pick-before-trial-end', iconStyle: 'check' },
  { key: 'superknit-band', iconStyle: 'check' },
  { key: 'warranty', iconStyle: 'check' },
];

export interface CollapsibleInfoSubsection {
  image: { url: string };
  heading: string;
  bodyText: { json: Document };
}

export const CONTENTFUL_DEFAULT = 'default';
export const CONTENTFUL_JOIN_FLOW_TYPE = 'join';
export const CONTENTFUL_UPGRADE_FLOW_TYPE = 'upgrade';
export type CONTENTFUL_FLOW_TYPE =
  | typeof CONTENTFUL_DEFAULT
  | typeof CONTENTFUL_JOIN_FLOW_TYPE
  | typeof CONTENTFUL_UPGRADE_FLOW_TYPE;

export interface CompareTableSubcopy {
  name: string;
  featureId: string;
  regionsCollection: { items: { countryCode: string }[] };
  compatibleTiersCollection: {
    items: {
      tierName: MembershipTierWithTrial;
    }[];
  };
  flow: {
    flowType: CONTENTFUL_FLOW_TYPE;
  };
  subcopy: { json: Document };
}

export interface PageDisclaimer {
  name: string;
  regionsCollection: { items: { countryCode: string }[] };
  compatibleTiersCollection: { items: { tierName: MembershipTierWithTrial }[] };
  flow: {
    flowType: CONTENTFUL_FLOW_TYPE;
  };
  content: { json: Document };
}

export interface PageDisclaimerSection {
  name: string;
  flow: {
    flowType: CONTENTFUL_FLOW_TYPE;
  };
  pageDisclaimersCollection: { items: PageDisclaimer[] };
}

export interface MembershipTierFeatureWithCTA {
  displayName: { json: Document };
  image: { url: string };
}

export interface MembershipTierComparisonFeatureList {
  tier: MembershipTierWithTrial;
  featureWithCta: MembershipTierFeatureWithCTA;
  featuresCollection: { items: MembershipTierComparisonFeature[] };
}

export interface MembershipTierCardImage {
  image: {
    url: string;
  };
  tier: {
    tierName: MembershipTier;
  };
}
export interface MembershipTierComparisonFeature {
  name: string;
  displayName: { json: Document };
  id: string;
  image: { url: string };
  region: (Region | typeof CONTENTFUL_DEFAULT)[];
}

export enum UPGRADE_STEPS {
  INELIGIBLE = 'UpgradeIneligible',
  SIGNIN = 'UpgradeSignin',
  UPGRADE = 'Upgrade',
  OPTIONS = 'Options',
  UPCYCLE = 'Upcycle',
  ACCESSORIES = 'UpgradeAccessories',
  ORDER = 'UpgradeOrder',
  RECEIPT = 'UpgradeReceipt',
}

export enum JOIN_FLOW_STEPS {
  JOIN = 'OPL',
  BANDS = 'Bands',
  MEMBERSHIP = 'Membership',
  ACCESSORIES = 'Accessories',
  ORDER = 'Order',
  RECEIPT = 'Receipt',
  FAMILY = 'Family',
}

// This is a mimic of the BE model and the values must match
// Model: join.upgrade.DevicesAndTiersResponse
export enum BillingType {
  STANDARD = 'STANDARD',
  FOUNDING_MEMBER = 'FOUNDING_MEMBER',
  FAMILY_ADMIN = 'FAMILY_ADMIN',
}

export const MG_DEFAULT_BAND_KEY = 'mg-obsidian-with-titanium-superknit-band';
export const PEAK_DEFAULT_BAND_KEY = '5-obsidian-superknit-band';
export const ONE_DEFAULT_BAND_KEY = '5-jet-black-coreknit-band';
export const TRIAL_DEFAULT_BAND_KEY = 'onyx-superknit-band';

// Typed in contentful, matches Membership Tier Feature 'id' field
export const PLACEHOLDER = 'Placeholder';

export type FeatureName = 'ECG' | 'BLOOD_PRESSURE' | 'IHRN';

export const FEATURE_AVAILABILITY_FEATURE_NAMES: Record<string, FeatureName> = {
  ECG: 'ECG',
  BLOOD_PRESSURE: 'BLOOD_PRESSURE',
  IHRN: 'IHRN',
};

export const PROMO_CODE_COOKIE_NAME = 'whoop-promo-code';

export const disclaimerFeatureIds = {
  GENERAL: 'General',
  IHRN: 'Irregular Heart Rhythm Notifications',
  ECG: 'ECG',
  HEALTHSPAN: 'Healthspan',
  BP_INSIGHTS: 'Blood Pressure Insights',
  UPGRADES: 'Upgrades',
  WHI: 'WHI',
};
